/**
 * OTPVerificationScreen
 *
 * Matches Figma node 12440-15568 — "Verification" screen.
 * Receives `email` via route param (set by LoginScreen when sending the OTP).
 * Delegates all state + API logic to useOTPFlow.
 */

import React, { useEffect } from "react";
import { View, TouchableOpacity, ScrollView } from "react-native";
import { KeyboardAwareScrollView } from "@/components/ui/KeyboardAvoidingView";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { AppText } from "@/components/ui/AppText";
import { Button } from "@/components/ui/Button";
import { OtpInput } from "@/components/ui/OTPInput";
import { useI18n } from "@/context/I18nContext";
import { useOTPFlow } from "@/features/auth/hooks/useOTPFlow";
import { toast } from "@/lib/toast";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { useIsWideScreen } from "@/hooks/useIsWideScreen";
import { Image } from "@/components/ui/Image";
import { PageFooter } from "@/components/ui/PageFooter";
import { Icon } from "@/components/ui/Icon";
import { Feather } from "@expo/vector-icons";

const OTP_HERO_IMAGE = require("@assets/images/login-background-image.png");

// ─── Form content (shared mobile + web) ──────────────────────────────────────

function OTPForm() {
  const { t, isRTL } = useI18n();
  const {
    code,
    isVerifying,
    error,
    countdown,
    canResend,
    isComplete,
    handleCodeChange,
    handleVerify,
    handleResend,
    router,
  } = useOTPFlow();

  useEffect(() => {
    if (!error) return;

    toast.error({
      message: t(error as TranslationKeys),
    });
  }, [error, t]);

  return (
    <View className="items-start w-full">
      {/* Heading */}
      <View className="gap-1 items-start w-full mb-10">
        <View className="w-full self-center items-start ">
          <Button
            className="hidden tablet:flex px-0 lg:px-0 py-2"
            label="auth.forgotPassword.goBack"
            onPress={router.back}
            variant="text"
            icon={
              <Icon
                as={Feather}
                name={isRTL ? "arrow-right" : "arrow-left"}
                size={16}
                className="text-primary"
              />
            }
          />
        </View>
        <AppText weight="semibold" className="text-xl tracking-tight text-dark-text-50">
          {t("auth.otp.heading")}
        </AppText>
        <AppText weight="medium" className="text-base text-dark-text-50">
          {t("auth.otp.subtitle")}
        </AppText>
      </View>

      {/* Form */}
      <View className="gap-4 items-start w-full mb-10">
        <View className="max-w-md w-full">
          <OtpInput
            value={code}
            onChangeText={handleCodeChange}
            hasError={!!error}
            editable={!isVerifying}
            testID="otp-input"
          />
        </View>

        {/* Error message */}
        {!!error && (
          <AppText role="alert" className="text-sm text-error">
            {t(error as TranslationKeys)}
          </AppText>
        )}

        {/* Resend row */}
        <View className="flex-row items-center gap-2 py-2">
          <AppText weight="medium" className="text-base text-supporting-text">
            {t("auth.otp.resendPrompt")}
          </AppText>
          <TouchableOpacity
            onPress={handleResend}
            disabled={!canResend}
            accessibilityRole="button"
            accessibilityLabel={t("auth.otp.resend")}
            testID="resend-button"
          >
            <AppText
              weight="semibold"
              className={`text-base ${!canResend ? "text-dark-text-10" : "text-primary"}`}
            >
              {countdown > 0
                ? t("auth.otp.resendCooldown", { seconds: countdown })
                : t("auth.otp.resend")}
            </AppText>
          </TouchableOpacity>
        </View>
      </View>

      <Button
        label="auth.otp.submit"
        variant={isComplete ? "primary" : "ghost"}
        disabled={!isComplete}
        loading={isVerifying}
        onPress={handleVerify}
        testID="submit-button"
        className="w-full"
      />
    </View>
  );
}

// ─── Screen ───────────────────────────────────────────────────────────────────

export default function OTPVerificationScreen() {
  const insets = useSafeAreaInsets();
  const isWide = useIsWideScreen();

  // ── Wide-screen web layout (≥768px): split image + form ────────────────────
  if (isWide) {
    return (
      <View className="flex-1 bg-white min-h-full">
        <View className="flex-1 flex-row">
          {/* Left image panel */}
          <Image
            source={OTP_HERO_IMAGE}
            className="flex-1 bg-blue-90 max-w-xs lg:max-w-md"
            contentFit="cover"
          />

          {/* Right scrollable form */}
          <ScrollView
            className="flex-1 bg-white"
            contentContainerStyle={{ flexGrow: 1 }}
            showsVerticalScrollIndicator={false}
          >
            <View className="flex-1 items-center justify-start py-12 px-8">
              <View className="max-w-md w-full">
                <OTPForm />
              </View>
            </View>
            <PageFooter />
          </ScrollView>
        </View>
      </View>
    );
  }

  // ── Mobile layout ────────────────────────────────────────────────────────
  return (
    <KeyboardAwareScrollView
      contentContainerStyle={{ flexGrow: 1, paddingBottom: Math.max(insets.bottom, 24) }}
      className="bg-white py-4 tablet:py-page-y"
    >
      <View className="self-center w-full tablet:max-w-3xl px-page-x">
        <OTPForm />
      </View>
    </KeyboardAwareScrollView>
  );
}
